/** Names of the Sidekick lifecycle events a host can track. */
export const SIDEKICK_LIFECYCLE_EVENT = {
  PANEL_OPENED: 'panelOpened',
  SESSION_STARTED: 'sessionStarted',
  QUERY_SUBMITTED: 'querySubmitted',
  RESPONSE_RECEIVED: 'responseReceived',
  FEEDBACK_SUBMITTED: 'feedbackSubmitted',
  SESSION_ENDED: 'sessionEnded',
} as const;

/**
 * A moment in the user's use of Sidekick. A session spans one
 * conversation while the panel is open; events inside it carry that
 * conversation's id so hosts can match them to backend traces.
 */
export type SidekickLifecycleEvent =
  | {
      type: typeof SIDEKICK_LIFECYCLE_EVENT.PANEL_OPENED;
      /** The open conversation, or `null` before the first question. */
      conversationId: string | null;
    }
  | {
      type: typeof SIDEKICK_LIFECYCLE_EVENT.SESSION_STARTED;
      conversationId: string;
    }
  | {
      type: typeof SIDEKICK_LIFECYCLE_EVENT.QUERY_SUBMITTED;
      conversationId: string;
      /** Character count of the question. */
      inputLength: number;
    }
  | {
      type: typeof SIDEKICK_LIFECYCLE_EVENT.RESPONSE_RECEIVED;
      conversationId: string;
      /** Backend trace of the answer, when the turn reported one. */
      traceId?: string;
      hasVisualization: boolean;
      /** Time from the question to the answer; absent without a question. */
      latencyMs?: number;
    }
  | {
      type: typeof SIDEKICK_LIFECYCLE_EVENT.FEEDBACK_SUBMITTED;
      conversationId: string;
      /** Backend trace of the rated answer, when it has one. */
      traceId?: string;
      thumbsUp: boolean;
    }
  | {
      type: typeof SIDEKICK_LIFECYCLE_EVENT.SESSION_ENDED;
      conversationId: string;
      /** Most messages the conversation held during the session. */
      messageCount: number;
      /** Whole seconds the session lasted, rounded up. */
      durationSeconds: number;
    };

/** Host callback that receives each Sidekick lifecycle event. */
export type SidekickLifecycleCallback = (event: SidekickLifecycleEvent) => void;
